<template>
  <AppDialog :open="open" @update:open="close" :title="t('nkf.interactions.idwgAlert')">
    <template #illustration
      ><AppDialogIllustration
        :src="illustration"
        background="var(--destructive-soft)"
        class="idwg-illustration"
    /></template>
    <template #title-actions
      ><Button
        variant="info-toggle"
        size="icon-xs"
        class="idwg-info"
        :aria-label="t('nkf.interactions.fullAlertInfo')"
        :aria-expanded="infoOpen"
        @click="infoOpen = !infoOpen"
        ><AppIcon size="action" name="info" /></Button
    ></template>
    <p
      v-if="infoOpen && !isGapAlert"
      class="tw:type-body-small tw:text-left tw:text-(--muted-foreground)"
    >
      {{ t('nkf.promptDetails.idwgAlertInfo') }}
    </p>
    <p
      v-if="infoOpen && isGapAlert"
      class="tw:type-body-small tw:text-left tw:text-(--muted-foreground)"
    >
      {{ t('nkf.promptDetails.gapAlertInfo', { days: assessment.gapThresholdDays }) }}
    </p>
    <div
      v-if="infoOpen && !isGapAlert"
      class="idwg-tiers tw:overflow-hidden tw:bg-(--card) tw:text-left tw:-mt-1 tw:border-[1.5px] tw:border-solid tw:border-(--border) tw:rounded-(--radius-note)"
      role="table"
    >
      <div
        class="idwg-tiers__row idwg-tiers__row--head tw:grid tw:items-center tw:bg-(--border-faint) tw:text-(--muted-foreground) tw:type-micro tw:gap-2 tw:py-2.5 tw:px-3 tw:border-t-0 tw:tracking-normal tw:uppercase"
        role="row"
      >
        <span role="columnheader">{{ t('nkf.promptDetails.tier') }}</span
        ><span role="columnheader">IDWG</span>
      </div>
      <div
        v-for="tier in dashboard.idwgBands"
        :key="tier.mainUnique"
        class="idwg-tiers__row tw:grid tw:items-center tw:type-caption tw:gap-2 tw:py-2.5 tw:px-3 tw:border-t tw:border-solid tw:border-(--border)"
        :class="{ 'idwg-tiers__row--current': assessment.band?.mainUnique === tier.mainUnique }"
        :style="{
          '--tier-color': tier.colorCode,
          '--tier-background': rgbaFromHex(tier.colorCode, 0.08),
        }"
        role="row"
        :aria-label="tier.name"
      >
        <span
          class="idwg-tiers__name tw:flex tw:items-center tw:min-w-0 tw:gap-1.5 tw:font-extrabold"
          role="cell"
          ><i
            class="tw:w-3 tw:h-3 tw:flex-none tw:rounded-[50%] tw:bg-(--success)"
            :style="{ background: tier.colorCode }"
          /><b class="tw:min-w-0" :title="tier.name">{{ tier.name }}</b></span
        ><span
          class="idwg-tiers__range tw:text-(--muted-foreground) tw:font-semibold"
          role="cell"
          >{{ range(tier) }}</span
        >
      </div>
    </div>
    <I18nT
      v-if="!isGapAlert && assessment.percent !== null"
      keypath="nkf.promptDetails.idwg"
      scope="global"
      tag="p"
      class="idwg-description tw:type-dialog-body tw:text-center tw:text-(--muted-foreground) tw:-mt-1.75"
    >
      <template #percent
        ><b :style="{ color: assessment.band?.colorCode }"
          >{{ assessment.percent.toFixed(1) }}%</b
        ></template
      >
      <template #threshold
        ><b>{{ READING_THRESHOLD.IDWG_HIGH }}%</b></template
      >
    </I18nT>
    <p v-if="isGapAlert" class="tw:type-dialog-body tw:text-center tw:text-(--muted-foreground)">
      {{ t('nkf.promptDetails.gapAlertDescription') }}
    </p>
    <AppDialogNote v-if="isGapAlert" icon="alert" tone="danger">{{
      t('nkf.promptDetails.attendanceAdvice')
    }}</AppDialogNote>
    <p
      v-else-if="assessment.band?.whatToDo"
      class="idwg-advice tw:type-advice tw:flex tw:bg-(--warning-soft) tw:text-(--warning-ink) tw:gap-2.75 tw:-my-0.5 tw:mx-0 tw:py-3.25 tw:px-3.5 tw:rounded-(--radius-note)"
    >
      <AppIcon size="action" name="bulb" /><span class="tw:min-w-0 tw:wrap-anywhere"
        ><b>{{ t('nkf.promptDetails.idwgAdviceTitle') }}</b> {{ assessment.band.whatToDo }}</span
      >
    </p>
    <template #secondary-actions>
      <Button
        variant="outline"
        :disabled="isClosing || !dialysisCentre?.telephone"
        @click="callCareTeam"
        ><AppIcon size="action" name="phone" />{{ t('nkf.promptDetails.callCareTeam') }}</Button
      >
    </template>
    <template #footer>
      <Button v-if="isGapAlert" :disabled="isClosing" @click="close">{{
        t('nkf.promptDetails.dismiss')
      }}</Button>
      <Button v-else :disabled="isClosing" @click="visit('/symptoms')">{{
        t('nkf.followup.check')
      }}</Button>
    </template>
  </AppDialog>
</template>
<script setup>
// 1. Import necessary components
import { useI18n, I18nT } from 'vue-i18n';
import { shallowRef, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useFluidStore } from '@/core/stores/fluid.store';
import { useAuthStore } from '@/core/stores/auth.store';
import { useNotificationsStore } from '@/core/stores/notifications.store';
import fluidService from '@/shared/services/fluid.service';
import { READING_THRESHOLD, FLUID_ALERT_REASON } from '@/features/fluid/constants/reading';
import AppDialog from '@/shared/components/AppDialog.vue';
import AppDialogNote from '@/shared/components/AppDialogNote.vue';
import AppDialogIllustration from '@/shared/components/AppDialogIllustration.vue';
import AppIcon from '@/shared/components/AppIcon.vue';
import { Button } from '@/shared/components/ui/button';
import { useCallSheet } from '@/shared/composables/useCallSheet';
import { rgbaFromHex } from '@/shared/utilities/color-utils';
// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const props = defineProps({ context: { type: Object, required: true } });
const emit = defineEmits(['dismissed']);
const { t } = useI18n();
const router = useRouter();
const { callNumber } = useCallSheet();
const fluid = useFluidStore();
const auth = useAuthStore();
const inbox = useNotificationsStore();
// 3. Define a reactive variable
const infoOpen = shallowRef(false);
const isClosing = shallowRef(false);
// 4. Define methods or computed properties if needed
const dialysisCentre = computed(() => auth.account.connectedFacilities);
const dashboard = computed(() => props.context.dashboard);
const assessment = computed(() => props.context.followUp ?? dashboard.value.assessment);
const isGapAlert = computed(() => assessment.value.reason === FLUID_ALERT_REASON.DIALYSIS_GAP);
const illustration = computed(() => {
  if (isGapAlert.value) {
    return '/images/nkf/gap.webp';
  }
  return '/images/nkf/idwg.webp';
});
function range(band) {
  if (band.fromPercent === null) {
    return t('nkf.promptDetails.tierRange.maximum', { to: band.toPercent });
  }
  if (band.toPercent === null) {
    return t('nkf.promptDetails.tierRange.minimum', { from: band.fromPercent });
  }
  return t('nkf.promptDetails.tierRange.between', {
    from: band.fromPercent,
    to: band.toPercent,
  });
}
async function dismiss() {
  if (isClosing.value) {
    return false;
  }
  isClosing.value = true;
  try {
    if (dashboard.value.alert && !dashboard.value.alert.dismissed) {
      const saved = await fluidService.dismissAlert(dashboard.value.alert.itemUnique);
      fluid.applyDashboard(saved.dashboard);
      return saved.dashboard;
    }
    return dashboard.value;
  } finally {
    isClosing.value = false;
  }
}
async function close() {
  const currentDashboard = await dismiss();
  if (currentDashboard) {
    emit('dismissed', currentDashboard);
    open.value = false;
    await inbox.refreshCount();
  }
}
async function visit(path) {
  const closed = await dismiss();
  if (!closed) {
    return;
  }
  open.value = false;
  await router.push(path);
  await inbox.refreshCount();
}
async function callCareTeam() {
  const closed = await dismiss();
  if (!closed) {
    return;
  }
  open.value = false;
  // A getMe refresh can remove the centre while dismissal is pending.
  if (dialysisCentre.value?.telephone) {
    callNumber(dialysisCentre.value.name, dialysisCentre.value.telephone);
  }
  await inbox.refreshCount();
}
// 5. VueJS Hooks
</script>
<style scoped>
.dialog-illustration.idwg-illustration {
  margin-bottom: -2px;
}
.idwg-advice svg {
  flex: none;
}
.idwg-tiers__row {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.8fr);
}
.idwg-tiers__row--current {
  background: var(--tier-background);
}
.idwg-tiers__row--current b {
  color: var(--tier-color);
}
.idwg-tiers__name b {
  font-weight: inherit;
}
</style>
